import React, { useState, useEffect } from 'react';
import { MessageCircle, Briefcase } from 'lucide-react';

const sampleCompanies = [
  {
    name: "Fireworks AI",
    logo: "/assets/company-display-photo-1.jpeg",
    industry: "Software Development",
    location: "Redwood City, CA",
    followers: "27k followers",
    jobs: "23 jobs"
  },
  {
    name: "Electra Therapeutics",
    logo: "/assets/company-display-photo-2.jpeg",
    industry: "Biotechnology Research",
    location: "",
    followers: "2k followers",
    tagline: "Electra Therapeutics is a clinical stage biotechnology company developing therapies that target signal regulatory proteins (SIRP) for the treatment of immunological diseases and cancer.",
    jobs: "76 jobs"
  },
  {
    name: "Lila Sciences",
    logo: "/assets/company-display-photo-3.jpeg",
    industry: "Technology, Information and Internet",
    location: "",
    followers: "24k followers",
    tagline: "Lila is a technology company pioneering the application of artificial intelligence to transform every aspect of the scientific method.",
    jobs: "55 jobs"
  }
];

const CompanyProfile = ({ company, zIndex, opacity }) => {
  // Larger vertical offset step to reveal more of the cards behind
  const step = 48; // px between stacked cards (reduced for mobile)
  const depth = 0.03; // scale reduction per step

  const offset = (3 - zIndex) * step;      // 0, 48, 96 for zIndex 3,2,1
  const scale = 1 - (3 - zIndex) * depth;  // 1, 0.97, 0.94

  return (
    <div 
      className="absolute left-0 right-0 transition-all duration-600 ease-out"
      style={{
        zIndex,
        opacity,
        transform: `translateY(${offset}px) scale(${scale})`
      }}
    >
      <div className="bg-white rounded-xl md:rounded-2xl shadow-xl overflow-hidden max-w-2xl mx-auto border border-gray-200">
        {/* Company Header */}
        <div className="p-3 md:p-6">
          <div className="flex items-start gap-2 md:gap-4">
            <div className="w-10 h-10 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-base md:text-2xl overflow-hidden flex-shrink-0">
              <img 
                src={company.logo} 
                alt={company.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-sm md:text-xl truncate">{company.name}</h3>
              <p className="text-gray-600 text-[10px] md:text-sm mt-0.5 md:mt-1 truncate">
                {company.industry} · {company.location}
              </p>
              <p className="text-gray-500 text-[10px] md:text-sm mt-1 md:mt-2">{company.followers}</p>
              {company.tagline && (
                <p className="text-gray-700 text-[10px] md:text-sm mt-1.5 md:mt-3 leading-relaxed line-clamp-2 md:line-clamp-none">{company.tagline}</p>
              )}
            </div>
          </div>
        </div>

        {/* Jobs Section */}
        <div className="px-3 md:px-6 pb-3 md:pb-6 border-t border-gray-100 pt-2 md:pt-4 mt-1 md:mt-2">
          <div className="flex items-center gap-1 md:gap-2 text-gray-700">
            <Briefcase className="w-3 h-3 md:w-5 md:h-5 text-gray-600 flex-shrink-0" />
            <span className="font-medium text-[10px] md:text-base">{company.jobs}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const LynkrDemoSection = () => {
  const [animationState, setAnimationState] = useState('idle');

  useEffect(() => {
    let timeouts = [];

    const runAnimation = () => {
      // Show user message
      timeouts.push(setTimeout(() => setAnimationState('user-message'), 500));
      
      // Show loading
      timeouts.push(setTimeout(() => setAnimationState('loading'), 2000));
      
      // Show companies
      timeouts.push(setTimeout(() => {
        setAnimationState('companies');
      }, 3500));
      
      // Reset and loop
      timeouts.push(setTimeout(() => {
        setAnimationState('idle');
      }, 9000));
      
      // Start over
      timeouts.push(setTimeout(runAnimation, 10000));
    };

    runAnimation();

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    <section className="py-4 md:py-8 px-2 md:px-6">
      <div className="max-w-7xl mx-auto">

        <div className="relative min-h-[400px] md:min-h-[700px]">
          {/* User Message - Top Right */}
          <div className="absolute top-0 right-0 max-w-[200px] md:max-w-md">
            <div 
              className={`transition-all duration-700 ${
                animationState !== 'idle' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              <div className="bg-white text-black rounded-2xl md:rounded-3xl rounded-tr-sm px-3 py-2 md:px-6 md:py-4 shadow-lg">
                <p className="text-xs md:text-lg">What are some Series A-C companies that raised recently?</p>
              </div>
              <div className="flex items-center gap-1 md:gap-2 mt-1 md:mt-2 justify-end mr-2 md:mr-4">
                <span className="text-xs md:text-sm text-gray-500">You</span>
                <MessageCircle className="w-3 h-3 md:w-4 md:h-4 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Response - Below User Message on Left */}
          <div className="absolute top-24 md:top-28 left-0 w-full max-w-3xl">
            {/* Loading State */}
            <div
              className={`absolute w-2/3 md:w-1/2 inset-x-0 top-0 z-10 transition-all duration-700 ${
                animationState === 'loading' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              <div className="bg-white rounded-2xl md:rounded-3xl rounded-tl-sm px-3 py-2 md:px-6 md:py-4 shadow-lg">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="flex gap-0.5 md:gap-1">
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-xs md:text-base text-gray-600">Gathering companies...</span>
                </div>
              </div>
              <div className="flex items-center gap-1 md:gap-2 mt-1 md:mt-2 ml-2 md:ml-4">
                <span className="text-xs md:text-sm text-gray-500">Lynkr</span>
              </div>
            </div>

            {/* Companies State */}
            <div
              className={`w-full md:w-4/5 absolute inset-x-0 top-0 z-20 transition-all duration-700 ${
                animationState === 'companies' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              {/* increased stack height so back cards have room to show */}
              <div className="relative h-[280px] md:h-[520px]">
                <CompanyProfile company={sampleCompanies[0]} zIndex={3} opacity={1} />
                <CompanyProfile company={sampleCompanies[1]} zIndex={2} opacity={0.7} />
                <CompanyProfile company={sampleCompanies[2]} zIndex={1} opacity={0.45} />
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default LynkrDemoSection;
